import { ChevronRight } from "lucide-react";
import Image from "next/image";
import clsx from "clsx";

/**
 * Enlace de acción compartido por las dos variantes de card.
 * El subrayado dorado se dibuja de izquierda a derecha al pasar el cursor:
 * sobre fondo claro el amarillo dibuja, nunca escribe (#FFB800 sobre blanco
 * da 1.9:1 y sería ilegible como texto).
 */
export function CardAction({ label }: { label: string }) {
  return (
    <span className="mt-1 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-[var(--fondoGeneral-secciones)]">
      <span className="relative">
        {label}
        <span className="absolute left-0 -bottom-0.5 h-[2px] w-full origin-left scale-x-0 bg-[var(--botonesSecundarios-iconoLlamativo)] transition-transform duration-300 ease-out group-hover:scale-x-100 motion-reduce:transition-none" />
      </span>
      <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
    </span>
  );
}

type MediaCardProps = {
  image: string;
  /** Se oculta si el backend todavía no envía título */
  title?: string;
  description?: string;
  actionLabel: string;
  onAction?: () => void;
  sizes?: string;
  priority?: boolean;
  className?: string;
  /**
   * Con `false` la card es sólo la imagen. Destacados lo usa porque su arte
   * ya lleva su propia tipografía y un cuerpo de texto la duplicaría.
   */
  showBody?: boolean;
};

/**
 * Card con imagen: media 16/10 arriba, cuerpo sólido debajo.
 * El texto va fuera de la foto para que la altura no dependa de la imagen
 * y la legibilidad no quede a merced de cada arte.
 */
export default function MediaCard({
  image,
  title,
  description,
  actionLabel,
  onAction,
  sizes = "(max-width: 640px) 85vw, (max-width: 1024px) 45vw, 410px",
  priority = false,
  className,
  showBody = true,
}: MediaCardProps) {
  return (
    <button
      type="button"
      onClick={onAction}
      // Sin cuerpo de texto no hay etiqueta visible, así que la card la lleva encima.
      aria-label={showBody ? undefined : (title ?? actionLabel)}
      className={clsx(
        "group flex w-full flex-col overflow-hidden rounded-2xl text-left cursor-pointer",
        "bg-[var(--blanco)] ring-1 ring-black/5 shadow-lg hover:shadow-2xl",
        "transition-all duration-500 hover:-translate-y-1 motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--botonesSecundarios-iconoLlamativo)] focus-visible:ring-offset-2",
        className,
      )}
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden">
        <Image
          src={image}
          alt={title ?? actionLabel}
          fill
          sizes={sizes}
          priority={priority}
          className="object-cover transition-transform duration-700 ease-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
        />
      </div>

      {showBody && (
      <div className="flex flex-1 flex-col gap-2 p-6">
        {title && (
          <h3 className="text-lg md:text-xl font-bold tracking-tight text-balance text-[var(--fondoGeneral-secciones)]">
            {title}
          </h3>
        )}
        {description && (
          <p className="text-sm leading-relaxed text-[var(--subtitulos-descripciones-iconos)] line-clamp-2">
            {description}
          </p>
        )}
        <div className="mt-auto">
          <CardAction label={actionLabel} />
        </div>
      </div>
      )}
    </button>
  );
}
